import React, { useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import axios from '~/data/http/axios'
import { alterFullpage, alterSnackbar } from '~/data/actions/siteActions'
import DomainManagement from '~/Common-Objects/DomainManagement'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import TrackingDomainManagement from '~/Common-Objects/DomainManagement/tracking'
import Template from './template'
import Overview from './overview'
import Analytics from './analytics'
import Visitors from './visitors'
import Settings from './settings'
import { validateAnalyticsOptions } from './settings/analytics'
import i18n from '~/i18n'
import helpers from '~/Classes/helpers'

export default function LandingPage() {
    const { id } = useParams()
    const history = useHistory()
    const dispatch = useDispatch()
    const site = useSelector((state) => state.siteMaster)
    const [landingPage, setLandingPage] = useState(null)
    const [shortCode, setShortCode] = useState(null)
    const [saving, setSaving] = useState(false)
    const [errors, setErrors] = useState({})
    const [domains, setDomains] = useState(null)
    const [sendingDomains, setSendingDomains] = useState(null)
    const [formsData, setFormsData] = useState(null)

    const getLandingPage = async () => {
        try {
            const res = await axios.get('/landingpage/' + id)
            setLandingPage(res.data)
            const forms = await axios.get('/landingPage/' + id + '/forms')
            setFormsData(forms.data)
        } catch (e) {
            console.log(e)
            history.push('/cp/landingpages')
        }
    }
    const saveLandingPage = async (_landingPage) => {
        setSaving(true)
        const errs = await validate(_landingPage || landingPage)
        setErrors(errs)
        if (Object.keys(errs).length) {
            setSaving(false)
            return
        }
        if (_landingPage) setLandingPage(_landingPage)
        try {
            await axios.put('/landingpage/' + id, _landingPage || landingPage)
            await getLandingPage()
            dispatch(alterSnackbar(true, 'Landing Page Saved'))
        } catch (e) {
            console.log(e)
            if (e?.data?.error === 'tierOverLimit' || e?.data?.message === 'tierOverLimit') {
                dispatch(alterSnackbar(true, i18n.t('form:landingPages.tierOverLimit')))
            } else if (e?.data?.error === 'tierForbids' || e?.data?.message === 'tierForbids') {
                dispatch(alterSnackbar(true, i18n.t('form:landingPages.customDomainUpsell')))
            } else {
                dispatch(alterSnackbar(true, e?.data?.error || e?.data?.message || 'Error saving landing page'))
            }
            await getLandingPage()
        }
        setSaving(false)
    }

    const getShortCode = async () => {
        const res = await axios.get('/accountMaster/shortCode')
        setShortCode(res.data.shortCode)
    }

    const validate = async (page) => {
        const errs = {}
        if (!page.slug) errs.slug = 'Required'
        else if (page.slug.length < 3) errs.slug = 'Must be at least 4 characters'
        // check it matches regex for strings, numbers and dashes only, lowercase, and must start with a letter
        else if (!/^[a-z][a-z0-9-]+$/.test(page.slug))
            errs.slug = 'Must start with a letter and can only contain lowercase letters, numbers, and dashes'

        if (page.options?.trackingDomain && page.options.trackingDomain !== -1) {
            // check its not also in use on another account
            try {
                const res = await axios.get('/landingpage/checkTrackingDomain/' + page.options.trackingDomain)
                if (!res.data.valid) {
                    errs.trackingDomain = `Domains connected to multiple Transpond accounts can't be used for Landing Pages`
                }
            } catch (e) {
                if (e?.data?.info?.state === 'notAuthenticated') {
                    errs.trackingDomain = (
                        <div>
                            {e?.data?.error}{' '}
                            <a
                                style={{
                                    color: 'var(--primary)',
                                    marginLeft: 2,
                                    textDecoration: 'underline',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    let domain = domains.find((d) => d.id === page.options.trackingDomain)?.domainName
                                    if (!domain) return
                                    const sendingDomain = sendingDomains.find((d) =>
                                        domain.includes('.' + d.domainName)
                                    )
                                    if (sendingDomain) {
                                        addTrackingDomain(domain)
                                    }
                                }}
                            >
                                Authenticate Domain
                            </a>
                        </div>
                    )
                } else {
                    const sendingDomain = sendingDomains.find(
                        (d) => d.trackingDomain?.id === page.options.trackingDomain
                    )
                    errs.trackingDomain = (
                        <div>
                            {e?.data?.error || 'Error validating domain'}{' '}
                            {!sendingDomain?.authenticated && (
                                <a
                                    style={{
                                        color: 'var(--primary)',
                                        marginLeft: 2,
                                        textDecoration: 'underline',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => addDomain(sendingDomain?.id)}
                                >
                                    Validate Domain
                                </a>
                            )}
                        </div>
                    )
                }
            }
        }

        if (page.options?.trackingDomain && page.options.trackingDomain === -1) {
            errs.trackingDomain = 'Please select a domain'
        }

        if (!errs.trackingDomain && !page.options?.trackingDomain) {
            // check if there are analytics providers enabled, if so remove them as they are not supported on our shared domain
            const providers = page.options?.analyticsProviders || []
            if (providers.length) {
                providers.forEach((provider) => {
                    delete page.options.analyticsProviders[provider]
                })
            }
            page.options.analyticsProviders = []
        }

        Object.assign(errs, validateAnalyticsOptions(page.options || {}))

        return errs
    }

    const getDomains = async () => {
        const res = await axios.get('/accountMaster/tracking?dupeCheck=1')
        setDomains(res.data.Domains)
        const sendingRes = await axios.get('/accountMaster/domain?returnAll=true&skipFavicon=1')
        setSendingDomains(sendingRes.data.domains)
    }

    const addTrackingDomain = (existingSubdomain) => {
        const drawer = (
            <TrackingDomainManagement
                existingSubdomain={typeof existingSubdomain === 'string' ? existingSubdomain : null}
                slug={landingPage.slug}
                finish={(id) => {
                    dispatch(alterFullpage(null))
                    if (id && typeof id === 'number') {
                        // reload tracking domains and select this one
                        getDomains()
                        const newLandingPage = {
                            ...landingPage,
                            options: {
                                ...landingPage.options,
                                trackingDomain: id
                            }
                        }
                        saveLandingPage(newLandingPage)
                    }
                }}
            />
        )
        dispatch(alterFullpage(drawer))
    }

    const addDomain = (domainId, step = 'addDomain') => {
        const drawer = (
            <DomainManagement
                forDomainId={typeof domainId === 'number' ? domainId : null}
                step={typeof step === 'string' ? step : 'addDomain'}
                finish={() => {
                    dispatch(alterFullpage(null))
                    getDomains()
                    validate(landingPage).then((errs) => {
                        setErrors(errs)
                    })
                }}
            />
        )
        dispatch(alterFullpage(drawer))
    }

    useEffect(() => {
        getShortCode()
        getDomains()
    }, [])

    useEffect(() => {
        getLandingPage()
    }, [id])

    if (!landingPage || !shortCode || !domains) return <LoadingIcons />

    let systemDomain
    if (site.siteId === 1 && window.location.hostname !== 'localhost') {
        systemDomain = 'https://transpond.site'
    } else {
        systemDomain = window.location.protocol + '//' + site.trackingDomain
    }

    const path = history.location.pathname.split('/')[4] || ''
    return (
        <Template
            landingPage={landingPage}
            LandingPageId={id}
            saving={saving}
            shortCode={shortCode}
            getLandingPage={getLandingPage}
            saveLandingPage={saveLandingPage}
            setLandingPage={setLandingPage}
            errors={errors}
            url={landingPage.url}
            formsData={formsData}
        >
            {/* do this instead of using routing as otherwise changes to LandingPage dont cascade down */}
            {path === 'settings' && (
                <Settings
                    landingPage={landingPage}
                    LandingPageId={id}
                    saving={saving}
                    shortCode={shortCode}
                    getLandingPage={getLandingPage}
                    saveLandingPage={saveLandingPage}
                    setLandingPage={setLandingPage}
                    errors={errors}
                    domains={domains}
                    addDomain={addDomain}
                    sendingDomains={sendingDomains}
                    url={landingPage.url}
                    addTrackingDomain={addTrackingDomain}
                />
            )}
            {path === 'analytics' && (
                <Analytics
                    landingPage={landingPage}
                    LandingPageId={id}
                    saving={saving}
                    shortCode={shortCode}
                    getLandingPage={getLandingPage}
                    saveLandingPage={saveLandingPage}
                    setLandingPage={setLandingPage}
                    errors={errors}
                    url={landingPage.url}
                />
            )}
            {path === 'visitors' && (
                <Visitors
                    landingPage={landingPage}
                    LandingPageId={id}
                    saving={saving}
                    shortCode={shortCode}
                    getLandingPage={getLandingPage}
                    saveLandingPage={saveLandingPage}
                    setLandingPage={setLandingPage}
                    errors={errors}
                />
            )}
            {!path && (
                <Overview
                    landingPage={landingPage}
                    LandingPageId={id}
                    saving={saving}
                    shortCode={shortCode}
                    getLandingPage={getLandingPage}
                    saveLandingPage={saveLandingPage}
                    setLandingPage={setLandingPage}
                    errors={errors}
                    url={landingPage.url}
                />
            )}
        </Template>
    )
}
